import { Component, OnInit, Output, EventEmitter, Input, HostBinding, HostListener, ElementRef, ChangeDetectionStrategy, AfterViewInit, signal, InjectionToken, inject, contentChild, viewChild } from "@angular/core";
import { CardComponent } from "../card/card.component";
import { KeyCodes } from "../../../../core-ui/services/keyboard.service";

export const MENU_ITEM_CONTAINER_TOKEN = new InjectionToken("MenItemContainer");

// Structural view of the parent `av-menu` that a menu item needs to talk to.
// Declared here (and consumed via `MENU_TOKEN`) so menu-item.component.ts does
// not have to import MenuComponent directly — that import used to form a
// circular edge with menu.component.ts which would TDZ-fault the webpack
// test bundle on bootstrap.
export interface IMenu {
  navigate(
    direction: "next" | "prev",
    currentItem: MenuItemComponent
  ): void;
  navigateIn(item: MenuItemComponent): void;
  navigateOut(): void;
}
export const MENU_TOKEN = new InjectionToken<IMenu>("Menu");

export interface IMenuItemContainer {
  getMenuItems(): MenuItemComponent[];
}

export interface ISubMenuPosition {
  placement: "right" | "left" | "overlay";
  // px `left` relative to the menu item, used only for the "overlay" placement
  offsetX: number;
  // px shift applied on top of the default vertical centering
  offsetY: number;
}

// Decides where a sub-menu goes so that it stays inside the viewport.
// Prefers the right side, then the left side, and when neither fits (mobile)
// it overlaps the parent menu, aligned to the right edge of the viewport.
export function computeSubMenuPosition(
  host: { left: number; right: number; top: number; height: number },
  sub: { width: number; height: number },
  viewport: { width: number; height: number },
  margin = 8
): ISubMenuPosition {
  let placement: ISubMenuPosition["placement"] = "right";
  let offsetX = 0;
  if (host.right + sub.width > viewport.width - margin) {
    if (host.left - sub.width >= margin) {
      placement = "left";
    } else {
      placement = "overlay";
      offsetX =
        Math.max(margin, viewport.width - margin - sub.width) - host.left;
    }
  }

  const defaultTop = host.top + host.height / 2 - sub.height / 2;
  const maxTop = viewport.height - margin - sub.height;
  const top = Math.max(margin, Math.min(defaultTop, maxTop));

  return { placement, offsetX, offsetY: top - defaultTop };
}

@Component({
  selector: "av-menu-item",
  templateUrl: "./menu-item.component.html",
  styleUrls: ["./menu-item.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class MenuItemComponent implements OnInit, AfterViewInit {
  private host = inject<ElementRef<HTMLDivElement>>(ElementRef);
  private card = inject(CardComponent, { optional: true });
  menu = inject<IMenu | null>(MENU_TOKEN, { optional: true });

  @Input() icon: string;
  @Input()
  set disabled(value: boolean) {
    this._disabled = value;
    this.host.nativeElement.setAttribute("aria-disabled", String(value));
  }
  get disabled(): boolean {
    return this._disabled;
  }
  @Input() color: "danger" | "success";
  @Input() arrow: boolean;
  @Input() arrowType: "toggle" | "submenu" = "submenu";
  @Input() expanded: boolean;
  @Input() active: boolean;
  @Input()
  set checked(value: boolean) {
    this._checked = value;
    this.host.nativeElement.setAttribute("aria-checked", String(value));
  }
  get checked(): boolean {
    return this._checked;
  }
  @Input() type: "header" | "separator" | "item" | "sub-item" | "radio";
  @Input() info: string;
  @Input() postfix: string;
  @Input() autoSubMenuPosition: boolean;
  @Input() loading: boolean;

  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() select: EventEmitter<void> = new EventEmitter();
  @Output() keyArrowRight: EventEmitter<void> = new EventEmitter();
  @Output() keyArrowLeft: EventEmitter<void> = new EventEmitter();
  @Output() keyArrowUp: EventEmitter<void> = new EventEmitter();
  @Output() keyArrowDown: EventEmitter<void> = new EventEmitter();

  readonly subMenu = contentChild(MENU_TOKEN);

  private _disabled: boolean;
  private _checked: boolean;

  isInCard: boolean;
  isItem: boolean;
  isHeader: boolean;

  @HostBinding("class") get class() {
    const cl = {
      "menu-item": this.type === "item" || this.type === "radio" || !this.type,
      "menu-item-separator": this.type === "separator",
      "menu-item-header": this.type === "header",
      "menu-sub-item": this.type === "sub-item",
      "menu-item-disabled": this.disabled,
      "menu-item-danger": this.color === "danger",
      "menu-item-success": this.color === "success",
      "menu-item-active": this.active,
      "menu-item-arrow": this.arrow,
      "menu-item-chevron-toggle": this.arrowType === "toggle",
      "menu-item-expanded": this.expanded,
      "menu-item-checked": this.checked,
      "menu-item-has-info": !!this.info,
      "menu-item-in-card": this.isInCard,
      "menu-item-loading": this.loading,
    };
    return cl;
  }

  /*
  // attempt to control focus and hover with code instead of css pseudo-class.
  // the :focus-within solved this issue but keeping the code just in case it's needed
  @HostListener("mouseenter")
  @HostListener("focus")
  onHoverOrFocus() {
    this.isSubMenuVisible.set(true);
  }
  @HostListener("blur", ["$event"])
  onBlur(event: FocusEvent) {
    // Check if the next focused element is within the submenu
    const relatedTarget = event.relatedTarget as HTMLElement;
    if (!relatedTarget || !relatedTarget.closest(".menu-item")) {
      this.isSubMenuVisible.set(false);
      console.log("blur", this.host.nativeElement);
    }
  }
  private get isFocused(): boolean {
    return (
      document.activeElement &&
      document.activeElement === this.host.nativeElement
    );
  }
  */

  @HostListener("mouseleave")
  onLeave() {
    this.host.nativeElement.blur();
    // if (!this.isFocused) {
    // this.isSubMenuVisible.set(false);
    // console.log("leave-blur", this.host.nativeElement);
    // }
  }

  @HostListener("keyup", ["$event"])
  onKeyUp(event: KeyboardEvent) {
    if (
      [KeyCodes.Space, KeyCodes.Enter, KeyCodes.NumpadEnter].includes(
        event.code as KeyCodes
      ) &&
      !this.disabled
    ) {
      this.select.emit();
      event.preventDefault();
    }
  }

  @HostListener("keydown", ["$event"])
  onKeyDown(event: KeyboardEvent) {
    if (!this.menu || this.disabled) return;
    const subMenu = this.subMenu();
    const subMenuValue = this.subMenu();
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        event.stopPropagation();
        if (this.keyArrowDown.observed) this.keyArrowDown.next();
        else this.menu.navigate("next", this);
        break;
      case "ArrowUp":
        event.preventDefault();
        event.stopPropagation();
        if (this.keyArrowUp.observed) this.keyArrowUp.next();
        else this.menu.navigate("prev", this);
        break;
      case "ArrowRight":
        if (!!subMenu || this.keyArrowRight.observed) {
          event.preventDefault();
          event.stopPropagation();
          if (this.keyArrowRight.observed) this.keyArrowRight.next();
          else if (!!subMenu) subMenu.navigateIn(this);
        }
        break;
      case "ArrowLeft":
        if (!!subMenuValue || this.keyArrowLeft.observed) {
          event.preventDefault();
          event.stopPropagation();
          if (this.keyArrowLeft.observed) this.keyArrowLeft.next();
          else if (!!subMenuValue) subMenuValue.navigateOut();
        }
        break;
      default:
        break;
    }
  }

  readonly labelRef = viewChild<ElementRef<HTMLDivElement>>("label");
  readonly arrowRef = viewChild<ElementRef<HTMLDivElement>>("arrow");

  @HostListener("click", ["$event"])
  onClick(e) {
    if (!this.disabled) {
      this.select.emit();
    }
  }

  readonly submenuRef = viewChild<ElementRef<HTMLDivElement>>("submenu");
  readonly subMenuPosition = signal<ISubMenuPosition | null>(null);

  // the sub-menu becomes visible through :hover / :focus-within, which are
  // already applied when these events fire, so it can be measured right away
  @HostListener("mouseenter")
  @HostListener("focusin")
  updateSubMenuPosition() {
    if (!this.autoSubMenuPosition || !this.subMenu()) return;
    const content = this.submenuRef()?.nativeElement.firstElementChild;
    if (!content) return;
    const subRect = content.getBoundingClientRect();
    if (!subRect.width) return;
    // the menu may live in the picture-in-picture window, measure against that
    const view = this.host.nativeElement.ownerDocument.defaultView ?? window;
    this.subMenuPosition.set(
      computeSubMenuPosition(
        this.host.nativeElement.getBoundingClientRect(),
        subRect,
        { width: view.innerWidth, height: view.innerHeight }
      )
    );
  }

  get subMenuTransform(): string | null {
    const offsetY = this.subMenuPosition()?.offsetY;
    return offsetY ? `translateY(calc(-50% + ${offsetY}px))` : null;
  }

  // get isSubMenuVisible() {
  // if (!this.subMenu) return;
  // return this.subMenu.parentCard?.host.nativeElement.style.display !== "none";
  // }

  ngOnInit() {
    let role;
    switch (this.type) {
      case "separator":
        role = "separator";
        break;
      case "header":
        role = "header";
        break;
      case "radio":
        role = "menuitemradio";
        break;
      case "item":
      case "sub-item":
      default:
        role = "menuitem";
        break;
    }
    this.host.nativeElement.setAttribute("role", role);

    if (this.arrow) {
      this.host.nativeElement.setAttribute("aria-haspopup", "menu");
    }

    this.isInCard = !!this.card;
    this.isItem = this.type === "item" || this.type === "radio" || !this.type;
    this.isHeader = this.type === "header";
  }

  ngAfterViewInit() {
    if (this.active && (this.isItem || this.type === "sub-item")) {
      this.focus();
    }
  }

  focus() {
    this.host.nativeElement.focus();
  }

  getNativeElement() {
    return this.host.nativeElement;
  }

  @HostBinding("attr.tabindex") get tabindex(): string | null {
    return this.disabled
      ? "-1"
      : this.isItem || this.type === "sub-item"
      ? "0"
      : "-1";
  }
}
